import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Alert,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  VolunteerActivism,
  ArrowBack,
  CheckCircle,
  Home,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { submitVolunteerApplication } from '../services/api';

interface VolunteerForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  days_available: string[];
  specific_times: string;
  volunteer_areas: string[];
  other_areas: string;
  conditions: string;
}

const VolunteerApplicationPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [countdown, setCountdown] = useState(20);

  const [formData, setFormData] = useState<VolunteerForm>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    days_available: [],
    specific_times: '',
    volunteer_areas: [],
    other_areas: '',
    conditions: '',
  });

  // Countdown effect for auto-redirect
  useEffect(() => {
    if (showThankYou && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showThankYou && countdown === 0) {
      navigate('/');
    }
  }, [showThankYou, countdown, navigate]);

  const steps = ['Personal Information', 'Availability', 'Volunteer Areas & Additional Information'];

  const daysOptions = [
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'saturday', label: 'Saturday' },
  ];

  const volunteerAreasOptions = [
    { value: 'packing_orders', label: 'Packing customer orders' },
    { value: 'sorting_food', label: 'Sorting bread, fruit & veggies' },
    { value: 'driver', label: 'Driver (truck and van pickup & deliveries)' },
    { value: 'ground_gardening', label: 'Ground person and gardening' },
    { value: 'tradesperson', label: 'Tradesperson' },
    { value: 'admin', label: 'Admin' },
    { value: 'accountant', label: 'Accountant' },
    { value: 'legal', label: 'Legal' },
    { value: 'public_relations', label: 'Public Relations' },
    { value: 'general_volunteering', label: 'General and any volunteering' },
    { value: 'other', label: 'Other (please write below)' },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleCheckboxChange = (field: 'days_available' | 'volunteer_areas', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    // Client-side validation
    const requiredFields = ['first_name', 'last_name', 'email', 'phone'];
    
    const missingFields = requiredFields.filter(field => !formData[field as keyof VolunteerForm] || formData[field as keyof VolunteerForm].toString().trim() === '');
    
    if (missingFields.length > 0) {
      setSubmitStatus({
        success: false,
        message: `Please fill in all required fields: ${missingFields.join(', ')}`,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitVolunteerApplication(formData);

      if (result.success) {
        setShowThankYou(true);
        setSubmitStatus({
          success: true,
          message: result.message,
        });
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          days_available: [],
          specific_times: '',
          volunteer_areas: [],
          other_areas: '',
          conditions: '',
        });
        setActiveStep(0);
      } else {
        setSubmitStatus({
          success: false,
          message: result.message || 'An error occurred. Please try again.',
        });
      }
    } catch (error: any) {
      console.error('Volunteer submission error:', error);
      
      // Handle validation errors
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = Object.values(validationErrors).flat().join(', ');
        setSubmitStatus({
          success: false,
          message: `Validation errors: ${errorMessages}`,
        });
      } else {
        setSubmitStatus({
          success: false,
          message: error.response?.data?.message || 'An error occurred while submitting your application. Please try again.',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Contact Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Days Available</FormLabel>
                <FormGroup>
                  <Grid container spacing={2}>
                    {daysOptions.map((day) => (
                      <Grid item xs={12} sm={6} key={day.value}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.days_available.includes(day.value)}
                              onChange={() => handleCheckboxChange('days_available', day.value)}
                            />
                          }
                          label={day.label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Specific times that you are available on the days above"
                name="specific_times"
                multiline
                rows={3}
                value={formData.specific_times}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Main areas where you would like to volunteer</FormLabel>
                <FormGroup>
                  <Grid container spacing={2}>
                    {volunteerAreasOptions.map((area) => (
                      <Grid item xs={12} sm={6} key={area.value}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.volunteer_areas.includes(area.value)}
                              onChange={() => handleCheckboxChange('volunteer_areas', area.value)}
                            />
                          }
                          label={area.label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <em>Volunteers also assist with general setting up, packing up, and cleaning. 
                Sorting fruit & veggies may require lifting and moving crates of produce (10kg or more)</em>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Other areas where you could assist"
                name="other_areas"
                multiline
                rows={3}
                value={formData.other_areas}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Any conditions to be considered"
                name="conditions"
                multiline
                rows={3}
                value={formData.conditions}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  // Thank you page
  if (showThankYou) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Box display="flex" justifyContent="center" mb={4}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />
          </Box>
          
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'success.main', mb: 3 }}>
            Thank You!
          </Typography>
          
          <Typography variant="h5" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
            Your volunteer application has been submitted successfully
          </Typography>
          
          

          <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
            <Typography variant="body2">
              <strong>What happens next?</strong> We'll review your application and contact you soon to 
              discuss your volunteer opportunities and arrange an orientation session. 
              Thank you for your interest in joining 
              our mission to serve the Gold Coast community.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/')}
              startIcon={<Home />}
            >
              Go to Home Page
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/volunteer')}
              startIcon={<ArrowBack />}
            >
              Back to Volunteer Page
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Redirecting to home page in {countdown} seconds...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/volunteer')}
          sx={{ mb: 2 }}
        >
          Back to Volunteer Page
        </Button>
        
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box display="flex" justifyContent="center" mb={3}>
            <VolunteerActivism sx={{ fontSize: 64, color: 'primary.main' }} />
          </Box>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Volunteer Application
          </Typography>
          <Typography variant="h5" color="text.secondary" paragraph sx={{ maxWidth: 800, mx: 'auto' }}>
            Complete your application to join our volunteer team
          </Typography>
        </Box>
      </Box>

      {/* Volunteer Times Notice */}
      <Box sx={{ mb: 4 }}>
        <Alert severity="info" sx={{ fontSize: '1rem' }}>
          <Typography variant="body2">
            <strong>Volunteer Times:</strong> The main times when we need volunteers are on Wednesdays & Thursdays from early morning to about 2pm.
          </Typography>
        </Alert>
      </Box>

      {/* Application Form */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          {submitStatus.message && !submitStatus.success && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitStatus.message}
            </Alert>
          )}
          
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 2 }}>
            {renderStepContent(activeStep)}
          </Box>
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              onClick={handleNext}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default VolunteerApplicationPage; 